import { saveCategoryValue } from '../../../services/resourceLifecycle';
import React, { useState, useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
} from '@mui/material';
import { useAttributeCategories, useAttributeOptions } from '../../../contexts/AttributesContext';
import { formatText } from '../../common/TextFormatterProvider';
import AppDatePicker from '../../common/AppDatePicker';
import {
  buildSearchText,
  canonicalizeTagValue,
  getTypedValueLabel,
  toSelectionOption,
  tagValueIdError,
} from '../../../utils/tagValueUtils';

// Attribute pairs attached to a whole workspace drop. Unlike TagSelector this is not
// table-scoped — triage has not chosen the destination tables yet — so every category
// is offered. Missing values are created inline (same value-document payload the
// table-scoped pickers write) so the pack can carry a brand-new submission reference.
// A Date category takes its value from the platform's calendar picker, as every other
// date input does: typed text admitted 2026-02-31, which the backend later refused (M209).
function PackAttributePicker({ companyId, value, onChange, disabled, isSaving, onSavingChange }) {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedValue, setSelectedValue] = useState(null);
  const [valueInput, setValueInput] = useState('');
  const [valueError, setValueError] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  // Values live in a subcollection the category read does not return, so they are
  // fetched on first use of a category and cached for the component's lifetime.

  const selectedTags = value || [];
  const controlsDisabled = disabled || isSaving;
  const { categories } = useAttributeCategories();
  const category = categories.find(cat => cat.id === selectedCategory) || null;

  // This picker sits in the long-lived upload panel, so both lists are live
  // (AttributesProvider): the categories, and the values of the selected one.
  const selectedCategoryType = category?.type || 'Text';
  const selectedTypeById = useMemo(
    () => (selectedCategory ? { [selectedCategory]: selectedCategoryType } : null),
    [selectedCategory, selectedCategoryType],
  );
  // Absent (not []) until the category's first snapshot: the value box must not
  // pose as an empty category meanwhile, or a typed value skips the match
  // against existing ones and lands as a near-duplicate.
  const categoryOptions = useAttributeOptions(selectedCategory || null, selectedTypeById)[selectedCategory];
  const valuesLoading = Boolean(selectedCategory) && categoryOptions === undefined;
  const categoryValues = categoryOptions || [];

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedValue(null);
    setValueInput('');
    setValueError('');
  };

  // One pair per category: a new value for an already-chosen category replaces it.
  const commitPair = (option) => {
    const newTags = selectedTags.filter(tag => tag.category !== selectedCategory);
    newTags.push({
      category: selectedCategory,
      value: option.value,
      label: getTypedValueLabel(option.value, option.type, option.name),
    });
    onChange(newTags);
    setSelectedValue(null);
    setValueInput('');
    setValueError('');
  };

  const handleAddPair = async () => {
    if (controlsDisabled || !category || valuesLoading) return;
    // An option picked from the list contributes its stored value verbatim — legacy
    // documents without `value` fall back through toSelectionOption, unre-canonicalised.
    if (selectedValue) {
      commitPair(selectedValue);
      return;
    }

    const typedValue = valueInput.trim();
    if (typedValue === '') {
      setValueError('Value cannot be empty');
      return;
    }
    // Case-insensitive match against the stored search text: a case variant of an
    // existing value selects that value instead of creating a near-duplicate.
    const existing = categoryValues.find(option =>
      String(option.search_text || option.name || option.id).toLowerCase() === typedValue.toLowerCase());
    if (existing) {
      commitPair(existing);
      if (existing.name !== typedValue) {
        setSnackbarMessage(`Matched existing value ${existing.name}`);
      }
      return;
    }

    let canonicalValue;
    try {
      canonicalValue = canonicalizeTagValue(typedValue, category.type);
    } catch (err) {
      setValueError(err.message || 'Enter a valid value');
      return;
    }
    if (canonicalValue === null) {
      setValueError('Value cannot be empty');
      return;
    }
    const displayValue = getTypedValueLabel(canonicalValue, category.type);
    const idError = tagValueIdError(displayValue);
    if (idError) {
      setValueError(idError);
      return;
    }
    const valueId = displayValue.trim().replace(/\s+/g, '_');

    onSavingChange(true);
    try {
      await saveCategoryValue(companyId, category.id, valueId, {
        name: displayValue,
        value: canonicalValue,
        search_text: buildSearchText(canonicalValue, displayValue),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      const newOption = toSelectionOption({
        name: displayValue,
        value: canonicalValue,
        search_text: buildSearchText(canonicalValue, displayValue),
        type: category.type,
      }, valueId);
      // The values listener delivers the new option; the pair is committed now.
      commitPair(newOption);
    } catch (err) {
      setValueError(err.message || 'Failed to add value');
    } finally {
      onSavingChange(false);
    }
  };

  const handleRemovePair = (categoryToRemove) => {
    onChange(selectedTags.filter(tag => tag.category !== categoryToRemove));
  };

  return (
    <Box>
      {selectedTags.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          {selectedTags.map((tag) => (
            <Chip
              key={tag.category}
              label={`${formatText(tag.category)}: ${formatText(String(tag.label))}`}
              onDelete={controlsDisabled ? undefined : () => handleRemovePair(tag.category)}
              size="small"
            />
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={controlsDisabled}>
          <InputLabel id="pack-attribute-category">Attribute</InputLabel>
          <Select
            labelId="pack-attribute-category"
            value={selectedCategory}
            label="Attribute"
            onChange={(e) => handleCategoryChange(e.target.value)}
          >
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {formatText(cat.name)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedCategoryType === 'Date' ? (
          <AppDatePicker
            label="Value"
            disabled={controlsDisabled || valuesLoading}
            value={valueInput}
            onChange={(nextDate) => {
              // The picked day follows the typed-value path above: an existing value
              // for that day is matched, otherwise one is created.
              setValueInput(nextDate);
              setSelectedValue(null);
              setValueError('');
            }}
            slotProps={{
              textField: {
                sx: { minWidth: 240, flexGrow: 1 },
                error: !!valueError,
                helperText: valueError,
              },
            }}
          />
        ) : (
        <Autocomplete
          freeSolo
          size="small"
          sx={{ minWidth: 240, flexGrow: 1 }}
          disabled={controlsDisabled || !selectedCategory || valuesLoading}
          options={categoryValues}
          value={selectedValue}
          onChange={(event, newValue) => {
            // freeSolo hands back the raw string on Enter — resolved when the pair
            // is added, so only a real option becomes the selection.
            setSelectedValue(typeof newValue === 'string' ? null : newValue);
            setValueError('');
          }}
          inputValue={valueInput}
          onInputChange={(event, newInputValue, reason) => {
            setValueInput(newInputValue);
            if (reason === 'input') {
              setSelectedValue(null);
              setValueError('');
            }
          }}
          getOptionLabel={(option) => (
            typeof option === 'string'
              ? option
              : formatText(getTypedValueLabel(option.value, option.type, option.name))
          )}
          filterOptions={(options, { inputValue }) => {
            const searchTerm = inputValue.toLowerCase();
            return options.filter(option =>
              String(option.search_text || option.name || option.id).toLowerCase().includes(searchTerm));
          }}
          isOptionEqualToValue={(option, selected) => option.id === selected.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {formatText(getTypedValueLabel(option.value, option.type, option.name))}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Value"
              placeholder={valuesLoading ? 'Loading values…' : 'Type to search or add a new value'}
              error={!!valueError}
              helperText={valueError}
            />
          )}
        />
        )}

        <Button
          variant="outlined"
          size="small"
          onClick={handleAddPair}
          disabled={controlsDisabled || !selectedCategory || valuesLoading || (!selectedValue && !valueInput.trim())}
          sx={{ height: 40 }}
        >
          {isSaving ? 'Adding...' : 'Add'}
        </Button>
      </Box>

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
      />
    </Box>
  );
}

export default PackAttributePicker;
